import { useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { ActivityForEditing } from '../types/ActivityTypes';
import { ActivityDto } from '../../../../../api/generated/data-contracts';

export const useActivityState = () => {
    const [editingItems, setEditingItems] = useState<ActivityForEditing[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const showSuccessFeedback = useCallback((identifier: string) => {
        setEditingItems(current =>
            current.map(item => {
                const itemId = item.isNew ? item.tempId : item.id;
                if (itemId === identifier) {
                    return { ...item, justSaved: true };
                }
                return item;
            }),
        );

        // Clear success indicator after 2 seconds
        setTimeout(() => {
            setEditingItems(current =>
                current.map(item => {
                    const itemId = item.isNew ? item.tempId : item.id;
                    if (itemId === identifier) {
                        return { ...item, justSaved: false };
                    }
                    return item;
                }),
            );
        }, 2000);
    }, []);

    const setItemLoadingState = useCallback((identifier: string, field: 'isSaving' | 'isDeleting', value: boolean) => {
        setEditingItems(current =>
            current.map(item => {
                const itemId = item.isNew ? item.tempId : item.id;
                if (itemId === identifier) {
                    return { ...item, [field]: value };
                }
                return item;
            }),
        );
    }, []);

    const addRecord = useCallback(() => {
        const newActivity: ActivityForEditing = {
            id: '',
            name: '',
            rate: null,
            rateCurrencyCode: '',
            isNew: true,
            tempId: uuid(),
        };
        setEditingItems(current => [...current, newActivity]);
    }, []);

    const updateEditingItem = useCallback((identifier: string, field: keyof ActivityDto, value: any) => {
        setEditingItems(current =>
            current.map(item => {
                const itemId = item.isNew ? item.tempId : item.id;
                if (itemId === identifier) {
                    return { ...item, [field]: value };
                }
                return item;
            }),
        );
    }, []);

    const removeNewItems = useCallback(() => {
        setEditingItems(current => current.filter(item => !item.isNew));
    }, []);

    const deleteLocalRecord = useCallback((tempId: string) => {
        setEditingItems(current => current.filter(item => item.tempId !== tempId));
    }, []);

    const syncWithActivities = useCallback((activities: ActivityDto[]) => {
        if (activities.length > 0) {
            setEditingItems(current => {
                // Keep new items and update existing ones
                const newItems = current.filter(item => item.isNew);
                const updatedExistingItems = activities.map(activity => ({ ...activity }));
                return [...updatedExistingItems, ...newItems];
            });
        }
    }, []);

    return {
        editingItems,
        errorMessage,
        setErrorMessage,
        showSuccessFeedback,
        setItemLoadingState,
        addRecord,
        updateEditingItem,
        removeNewItems,
        deleteLocalRecord,
        syncWithActivities,
    };
};
